/**
 * IL FEED DEL COACH «Valuta le mie mosse» (spec-20b §1, §2): cosa arriva dal socket del tavolo e il comando che lo accende.
 *
 * ⛔ QUESTO MODULO ESISTE SOLO NEL RAMO PIEGATO DEL FLAG. Lo importa `contexts/ActiveMatchContext.tsx` e lo usa solo dentro un
 * confronto LETTERALE `import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true'` (non una costante esportata: il
 * bundler non la piega, overview TMA «Due lezioni di BUILD e di BANCO», (a)). In produzione il modulo non entra nella build.
 *
 * Il socket e' quello del tavolo (`useBackgammonSocket` di board-ui 0.17.0): la TMA non ne apre un secondo (un secondo
 * `match:join` lega il chiamante a un posto). Gli eventi arrivano dalla mappa `listeners`, e due regole di board-ui la
 * governano (spec §1): l'insieme degli eventi si fissa alla CREAZIONE del socket, quindi la mappa ha TUTTE le sue chiavi
 * (`coach:verdict`, `coach:history`) fin dal primo render, costante per build e anche a coach spento lato server; e nessuna
 * chiave e' un evento che il hook gia' gestisce.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CoachHistoryPayloadSchema,
  CoachVerdictPayloadSchema,
  MatchHistoryPayloadSchema,
  type MatchHistoryPayload,
} from '@qlashgeminiz/qgammon-types';
import type { PauseCommandResult } from '@qlashgeminiz/qgammon-board-ui';

import { inserisciVerdetto, sostituisciStoria, type VerdettoCoach } from '@/lib/qgammon/coachGiudizio';
import type { StatoCronologia } from '@/lib/qgammon/cronologia';
import { leggiCoach, scriviCoach } from '@/lib/qgammon/coachPreferenza';

/** Cio' che la TMA sa del coach per UNA partita. */
export interface StatoCoach {
  matchId: string | null;
  /** Il server ha mandato `coach:history` almeno una volta per questa partita. Senza, il coach non e' acceso lato server. */
  ricevuto: boolean;
  /** La partita ammette il coach (v1: solo la pratica contro il bot, per il mio posto). L'interruttore c'e' solo se vero. */
  allowed: boolean;
  /** Il coach del MIO posto e' acceso, come lo dice il server (e come l'ack di `coach:set` l'ha cambiato). */
  enabled: boolean;
  /** Ad ogni `coach:history` sale di uno: e' la «connessione» per cui l'accensione automatica parte UNA volta. */
  consegne: number;
  verdetti: readonly VerdettoCoach[];
  /**
   * `allowed` della PRIMA consegna di `coach:history` per questa partita (la join), o `null` se non e' ancora arrivata. La cronologia mostra il
   * coach solo se era vero GIA' alla join: una partita senza coach alla join non lo prende dopo da un evento successivo (V7 a) di tma-orcr,
   * condizione (2): la conversione passa da un consenso esplicito, che la TMA oggi non ha).
   */
  allowedAllaJoin: boolean | null;
  /** I verdetti dell'ultima `coach:history` (la storia CONSEGNATA dal server): dopo una revoca dell'allenamento la cronologia mostra solo
   * questi e quelli visti prima della revoca, mai un `coach:verdict` arrivato dopo (V7 a rev.3 di tma-orcr, 22:26Z). */
  verdettiStoria: readonly VerdettoCoach[];
  /** L'ultima `match:history` di questa partita (la cronologia, contratto ritmo-bot §5): sostituita a ogni join, mai sommata. */
  storia: MatchHistoryPayload | null;
}

const STATO_VUOTO: StatoCoach = {
  matchId: null,
  ricevuto: false,
  allowed: false,
  enabled: false,
  consegne: 0,
  verdetti: [],
  allowedAllaJoin: null,
  verdettiStoria: [],
  storia: null,
};

export interface FeedCoach {
  /** La mappa per `useBackgammonSocket({ listeners })`: STESSE chiavi a ogni render, MAI un evento del hook. */
  listeners: Readonly<Record<string, (payload: unknown) => void>>;
  stato: StatoCoach;
  /** Dopo un `ok` dell'ack: il server non rimanda `coach:history` al cambio, quindi il valore a schermo lo aggiorna l'ack. */
  impostaAcceso: (enabled: boolean) => void;
}

/**
 * Il feed. `matchId` e' la partita attiva (o `null`): un evento di un'altra partita si scarta (e con `null` ogni evento: il socket
 * e' chiuso), e quando la partita cambia lo stato si legge come vuoto (un rimontaggio SOSTITUISCE, non accumula). A partita
 * finita (`null` dopo una partita) l'ultimo stato resta leggibile.
 */
export function useCoachFeed(matchId: string | null): FeedCoach {
  const [grezzo, setGrezzo] = useState<StatoCoach>(STATO_VUOTO);
  const matchRef = useRef<string | null>(matchId);
  useEffect(() => {
    matchRef.current = matchId;
  }, [matchId]);

  const listeners = useMemo<Readonly<Record<string, (payload: unknown) => void>>>(
    () => ({
      'coach:history': (payload) => {
        const r = CoachHistoryPayloadSchema.safeParse(payload);
        if (!r.success || r.data.matchId !== matchRef.current) return;
        const h = r.data;
        setGrezzo((prima) => {
          const base = prima.matchId === h.matchId ? prima : STATO_VUOTO;
          return {
            matchId: h.matchId,
            ricevuto: true,
            allowed: h.allowed,
            enabled: h.enabled,
            consegne: base.consegne + 1,
            verdetti: sostituisciStoria(base.verdetti, h),
            verdettiStoria: sostituisciStoria(base.verdetti, h),
            allowedAllaJoin: base.allowedAllaJoin ?? h.allowed,
            storia: base.storia,
          };
        });
      },
      // La cronologia (types 0.23): NON e' un evento del coach e non accende niente; passa di qui perche' la mappa `listeners` e' una sola
      // per socket e vive solo nel ramo piegato del flag. Non segna `ricevuto` (quello dice se il COACH e' acceso lato server).
      'match:history': (payload) => {
        const r = MatchHistoryPayloadSchema.safeParse(payload);
        if (!r.success || r.data.matchId !== matchRef.current) return;
        const h = r.data;
        setGrezzo((prima) => ({ ...(prima.matchId === h.matchId ? prima : { ...STATO_VUOTO, matchId: h.matchId }), storia: h }));
      },
      'coach:verdict': (payload) => {
        const r = CoachVerdictPayloadSchema.safeParse(payload);
        if (!r.success || r.data.matchId !== matchRef.current) return;
        const v = r.data;
        setGrezzo((prima) => {
          const base = prima.matchId === v.matchId ? prima : { ...STATO_VUOTO, matchId: v.matchId };
          const verdetti = inserisciVerdetto(base.verdetti, v);
          return verdetti === base.verdetti && base === prima ? prima : { ...base, verdetti };
        });
      },
    }),
    [],
  );

  const impostaAcceso = useCallback((enabled: boolean) => {
    setGrezzo((prima) => (prima.enabled === enabled ? prima : { ...prima, enabled }));
  }, []);

  // `matchId` null = nessuna partita attiva: o non ce n'e' mai stata (stato vuoto), o e' appena finita e il socket e' stato chiuso
  // (`clearActiveMatch()` a `ended`): la schermata di fine resta, e lo storico con lei. Il filtro per partita lo fa chi legge (`riferimentoCoincide`).
  const stato = matchId === null || grezzo.matchId === matchId ? grezzo : STATO_VUOTO;
  return useMemo(() => ({ listeners, stato, impostaAcceso }), [listeners, stato, impostaAcceso]);
}

/** Il coach come lo vede il tavolo: lo stato, e il gesto di accenderlo o spegnerlo. */
export interface CoachDelTavolo {
  stato: StatoCoach;
  /** Il comando e' in volo: l'interruttore e' fermo, un secondo tocco non parte. */
  inAttesa: boolean;
  /** Il codice del rifiuto dell'ultimo tentativo, o `null`. La frase la sceglie `chiaveRifiutoCoach`. */
  rifiuto: string | null;
  /** Il gesto dell'utente: ricorda la scelta sul dispositivo e manda `coach:set`. */
  cambia: (enabled: boolean) => void;
  /**
   * La CRONOLOGIA della partita attiva (D5 della revisione di #957): si accumula nel PROVIDER, sopra le rotte, cosi' chi esce dal tavolo e ci
   * rientra senza una nuova join ritrova i turni visti dal socket intanto. Assente = il provider non la calcola (un doppio di test).
   */
  cronologia?: StatoCronologia | null;
}

type SocketDelCoach = {
  connected: boolean;
  command: (event: string, payload?: Record<string, unknown>) => Promise<PauseCommandResult>;
  /** L'ultimo `match:state`, grezzo (board-ui non lo valida): serve a sapere se la partita e' un ALLENAMENTO fra amici. */
  state?: unknown;
};

/**
 * D2 della revisione di #959: la partita e' un allenamento fra amici se lo snapshot di QUESTA partita porta `allenamento` (attivo o revocato).
 * Il server manda `match:state` alla join PRIMA di `coach:history` (QGammon e3aa9f9 handlers.ts, `match:join`), quindi alla consegna lo
 * snapshot c'e' gia'.
 */
function partitaInAllenamento(state: unknown, matchId: string | null): boolean {
  if (matchId === null || !state || typeof state !== 'object') return false;
  const s = state as { matchId?: unknown; allenamento?: unknown };
  return s.matchId === matchId && s.allenamento !== undefined && s.allenamento !== null;
}

/** L'UNICO punto che manda `coach:set` (il verificatore pretende che `command(` si chiami solo con questo evento). */
export function mandaCoachSet(sock: Pick<SocketDelCoach, 'command'>, enabled: boolean): Promise<PauseCommandResult> {
  return sock.command('coach:set', { enabled });
}

/**
 * Il comando e l'accensione automatica. L'accensione automatica: con `allowed && !enabled` e la preferenza del dispositivo a
 * `'1'`, UN solo `coach:set {enabled:true}` per (partita, consegna di `coach:history`) — mai in loop, e mai se l'utente
 * l'ha appena spento (il suo gesto scrive `'0'`).
 * ⛔ Mai in un ALLENAMENTO fra amici (D2 della revisione di #959; Luca, rev.6: «l'interruttore personale di valutazione resta OFF
 * inizialmente»): li' il tutor si accende solo col gesto, anche se la preferenza del dispositivo e' rimasta '1' da una pratica col bot.
 */
export function useCoachComando(feed: FeedCoach, sock: SocketDelCoach): CoachDelTavolo {
  const [inAttesa, setInAttesa] = useState(false);
  const inVolo = useRef(false);
  const [rifiuto, setRifiuto] = useState<string | null>(null);
  const sockRef = useRef(sock);
  useEffect(() => {
    sockRef.current = sock;
  }, [sock]);
  const { impostaAcceso } = feed;

  const invia = useCallback(
    async (enabled: boolean) => {
      if (inVolo.current) return;
      inVolo.current = true;
      setInAttesa(true);
      setRifiuto(null);
      const esito = await mandaCoachSet(sockRef.current, enabled);
      inVolo.current = false;
      setInAttesa(false);
      if (esito.ok) impostaAcceso(enabled);
      else setRifiuto(esito.code);
    },
    [impostaAcceso],
  );

  const cambia = useCallback(
    (enabled: boolean) => {
      scriviCoach(enabled);
      void invia(enabled);
    },
    [invia],
  );

  const { matchId, allowed, enabled, consegne } = feed.stato;
  const fatta = useRef<string | null>(null);
  const connessa = sock.connected;
  const inAllenamento = partitaInAllenamento(sock.state, matchId);
  useEffect(() => {
    if (matchId === null || !allowed || enabled || !connessa || inAllenamento) return;
    const chiave = `${matchId}|${consegne}`;
    if (fatta.current === chiave) return;
    if (!leggiCoach()) return;
    fatta.current = chiave;
    void invia(true);
  }, [matchId, allowed, enabled, consegne, connessa, inAllenamento, invia]);

  return useMemo(() => ({ stato: feed.stato, inAttesa, rifiuto, cambia }), [feed.stato, inAttesa, rifiuto, cambia]);
}
